/**
 * WARNING: The values for the Breakpoint enum are used as props on the
 * {@link import('~/ui/modules/image').Image|Image} component. Changing
 * them will have downstream effects.
 */

import breakpointStyles from './breakpoints.module.scss';
import gridStyles from './grid.module.scss';

export enum Breakpoint {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  LAPTOP = 'laptop',
  DESKTOP = 'desktop',
}

/** Represents an object where each breakpoint has a value of type `T` */
export type ByBreakpoint<T> = Record<Breakpoint, T>;

/**
 * Represents a value used by {@link import('~/ui/components/grid/column').Column|Column} for the
 * column span and offsets.
 */
export type ColumnValue<T> = Partial<ByBreakpoint<T>>;

export const MAX_GRID_WIDTH = parseInt(gridStyles.maxGridWidth, 10);

/**
 * Number of columns for each breakpoint. Nested grids may have fewer columns.
 * @enum {number}
 */
export const Columns: ByBreakpoint<number> = {
  [Breakpoint.MOBILE]: parseInt(gridStyles.columnsMobile, 10),
  [Breakpoint.TABLET]: parseInt(gridStyles.columnsTablet, 10),
  [Breakpoint.LAPTOP]: parseInt(gridStyles.columnsTablet, 10),
  [Breakpoint.DESKTOP]: parseInt(gridStyles.columnsDesktop, 10),
} as const;

/**
 * Minimum viewport width for each breakpoint.
 * @enum {number}
 */
export const MinWidth: ByBreakpoint<number> = {
  [Breakpoint.MOBILE]: 0,
  [Breakpoint.TABLET]: parseInt(breakpointStyles.tablet, 10),
  [Breakpoint.LAPTOP]: parseInt(breakpointStyles.laptop, 10),
  [Breakpoint.DESKTOP]: parseInt(breakpointStyles.desktop, 10),
} as const;

/**
 * Page margin for each breakpoint.
 * @enum {number}
 */
export const Margin: ByBreakpoint<number> = {
  [Breakpoint.MOBILE]: parseInt(gridStyles.marginMobile, 10),
  [Breakpoint.TABLET]: parseInt(gridStyles.marginTablet, 10),
  [Breakpoint.LAPTOP]: parseInt(gridStyles.marginLaptop, 10),
  [Breakpoint.DESKTOP]: parseInt(gridStyles.marginDesktop, 10),
} as const;

/**
 * Media query (including min- and max-width as needed) for each breakpoint.
 * @enum {string}
 */
export const MediaQuery: ByBreakpoint<string> = {
  [Breakpoint.MOBILE]: `(max-width: ${MinWidth[Breakpoint.TABLET] - 1}px)`,
  [Breakpoint.TABLET]: `(min-width: ${MinWidth[Breakpoint.TABLET]}px) and (max-width: ${MinWidth[Breakpoint.LAPTOP] - 1}px)`,
  [Breakpoint.LAPTOP]: `(min-width: ${MinWidth[Breakpoint.LAPTOP]}px) and (max-width: ${MinWidth[Breakpoint.DESKTOP] - 1}px)`,
  [Breakpoint.DESKTOP]: `(min-width: ${MinWidth[Breakpoint.DESKTOP]}px)`,
} as const;
